import { CaretLeft, CaretRight } from "@phosphor-icons/react";
import { useTranslation } from "react-i18next";

/**
 * Reusable slider header with prev/next arrows.
 *
 * Why this exists:
 * Flex auto-reversal in RTL was causing subtle bugs across every slider:
 *   - The visible-left button sometimes stayed bound to slidePrev in Arabic,
 *     so clicking "<" at slide 0 did nothing — matching English boundary
 *     behavior instead of RTL reading-direction (where "<" should advance).
 *   - The same pattern repeated in CategorySlider, InstructorProfileSlider,
 *     LatestCourses, etc.
 *
 * This component freezes the arrow row to dir="ltr" so the layout is
 * deterministic (`<` always on the left, `>` always on the right), and flips
 * the click handlers based on language. The title `<h2>` keeps its own dir
 * so Arabic text still renders right-to-left inside the heading.
 *
 * Props:
 *  - title: string — section title
 *  - onPrev / onNext: () => void — slidePrev / slideNext on the Swiper instance
 *  - arrowsClassName: string — optional class override for arrow buttons
 *  - hideArrowsOnMobile: boolean — default true (hides arrows <md)
 *  - titleClassName: string — optional class override for the h2
 */
const SliderNav = ({
  title,
  onPrev,
  onNext,
  arrowsClassName = "bg-black text-xl md:text-2xl lg:text-3xl text-white p-0.5 md:p-1",
  hideArrowsOnMobile = true,
  titleClassName = "text-white text-center text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold",
}) => {
  const { i18n } = useTranslation();
  const isRTL = i18n.language === "ar";

  // In Arabic, reading advances LEFT, so clicking "<" must call onNext
  // (advance) and ">" must call onPrev (go back). This flips the boundary
  // behavior: at slide 0 in RTL, ">" is the dead-end, not "<".
  const onLeftClick = isRTL ? onNext : onPrev;
  const onRightClick = isRTL ? onPrev : onNext;

  const arrowBaseCls = `slider-arrow items-center justify-center transition-all duration-300 hover:scale-[0.9] ${
    hideArrowsOnMobile ? "hidden md:flex" : "flex"
  } ${arrowsClassName}`;

  return (
    <div
      dir="ltr"
      className="flex items-center gap-x-2 md:gap-x-4 lg:gap-x-6 w-full justify-center"
    >
      <button
        type="button"
        onClick={onLeftClick}
        aria-label={isRTL ? "Next slide" : "Previous slide"}
        className={arrowBaseCls}
      >
        <CaretLeft />
      </button>
      <h2 dir={isRTL ? "rtl" : "ltr"} className={titleClassName}>
        {title}
      </h2>
      <button
        type="button"
        onClick={onRightClick}
        aria-label={isRTL ? "Previous slide" : "Next slide"}
        className={arrowBaseCls}
      >
        <CaretRight />
      </button>
    </div>
  );
};

export default SliderNav;
